import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ApiError } from '@/api/client';
import { useCreateWorkspace } from '@/api/workspaces';
import type { WorkspacePlan } from '@/api/types';
import { Button, Input, Modal, Select, useToast } from '@/components/ui';
import { humanize } from '@/lib/format';
import { PLANS, slugify } from './meta';

const schema = z.object({
  name: z.string().trim().min(2, 'At least 2 characters').max(120),
  slug: z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^$|^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/, 'Lowercase letters, digits and dashes'),
  plan: z.enum(['trial', 'starter', 'growth', 'enterprise']),
  website: z.string().trim().url('Enter a full URL, e.g. https://acme.com').or(z.literal('')),
  ownerName: z.string().trim().min(1, 'Name is required').max(120),
  ownerEmail: z.string().trim().email('Enter a valid email'),
  ownerPassword: z.string().min(10, 'At least 10 characters').max(128),
});
type FormValues = z.infer<typeof schema>;

/** Super admin: onboard a new client — its workspace plus the owner account that runs it. */
export function ClientFormModal({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const create = useCreateWorkspace();
  const {
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      slug: '',
      plan: 'trial',
      website: '',
      ownerName: '',
      ownerEmail: '',
      ownerPassword: '',
    },
  });
  const slugPreview = watch('slug') || slugify(watch('name'));

  const onSubmit = handleSubmit((v) =>
    create.mutate(
      {
        name: v.name,
        slug: v.slug || undefined,
        plan: v.plan as WorkspacePlan,
        website: v.website || null,
        owner: { name: v.ownerName, email: v.ownerEmail, password: v.ownerPassword },
      },
      {
        onSuccess: ({ workspace, owner }) => {
          toast.success(`${workspace.name} is live — ${owner.email} can sign in now`);
          onClose();
        },
        onError: (err) => {
          if (err instanceof ApiError && err.status === 409) {
            if (/slug/i.test(err.message)) setError('slug', { message: err.message });
            else setError('ownerEmail', { message: 'This email already has an account' });
            return;
          }
          if (err instanceof ApiError) {
            const map: Record<string, keyof FormValues> = {
              'owner.email': 'ownerEmail',
              'owner.name': 'ownerName',
              'owner.password': 'ownerPassword',
            };
            const fields = Object.entries(err.fieldErrors);
            for (const [path, message] of fields)
              setError(map[path] ?? (path as keyof FormValues), { message });
            if (fields.length) return;
          }
          toast.error(err.message);
        },
      },
    ),
  );

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title="New client"
      description="Creates an isolated workspace and its owner. The owner then adds their own admins and agents."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="client-form" loading={create.isPending}>
            Create client
          </Button>
        </>
      }
    >
      <form id="client-form" onSubmit={onSubmit} noValidate className="space-y-6">
        <fieldset className="grid gap-4 sm:grid-cols-2">
          <legend className="mb-3 text-sm font-semibold text-slate-900">Company</legend>
          <Input label="Client name" required error={errors.name?.message} {...register('name')} />
          <Input
            label="Workspace slug"
            placeholder={slugify(watch('name')) || 'acme'}
            hint={slugPreview ? `Handle: ${slugPreview}` : 'Defaults to a slug of the name'}
            error={errors.slug?.message}
            {...register('slug')}
          />
          <Select label="Plan" error={errors.plan?.message} {...register('plan')}>
            {PLANS.map((p) => (
              <option key={p} value={p}>
                {humanize(p)}
              </option>
            ))}
          </Select>
          <Input
            label="Website"
            placeholder="https://acme.com"
            error={errors.website?.message}
            {...register('website')}
          />
        </fieldset>
        <fieldset className="grid gap-4 sm:grid-cols-2">
          <legend className="mb-3 text-sm font-semibold text-slate-900">Workspace owner</legend>
          <Input label="Full name" required error={errors.ownerName?.message} {...register('ownerName')} />
          <Input
            label="Email"
            type="email"
            required
            autoComplete="off"
            error={errors.ownerEmail?.message}
            {...register('ownerEmail')}
          />
          <Input
            label="Temporary password"
            type="password"
            required
            autoComplete="new-password"
            hint="Min 10 characters. Ask them to change it after first sign-in."
            error={errors.ownerPassword?.message}
            {...register('ownerPassword')}
          />
        </fieldset>
      </form>
    </Modal>
  );
}
